import type {
  JobCreateRequest,
  JobCreateAcceptedResponse,
  JobCreationStatusResponse,
} from '@techgitter/contracts';

const API_BASE_URL = '/api';

export class JobCreationError extends Error {
  constructor(
    public statusCode: number,
    public error: string,
    message: string
  ) {
    super(message);
    this.name = 'JobCreationError';
  }
}

export async function createJob(
  payload: JobCreateRequest,
  signal?: AbortSignal
): Promise<JobCreateAcceptedResponse> {
  const response = await fetch(`${API_BASE_URL}/jobs`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
    signal,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({
      error: `Failed to submit job: ${response.status}`,
      status: response.status,
    }));
    throw new JobCreationError(response.status, errorData.error, errorData.error);
  }

  return response.json();
}

export interface JobUpdatePayload {
  rsTicketId?: string;
  subject?: string;
  jobType?: string;
  status?: string;
  dvOverlay?: Record<string, unknown>;
}

export async function updateJob(
  jobGuid: string,
  payload: JobUpdatePayload,
  signal?: AbortSignal
): Promise<JobCreateAcceptedResponse> {
  const response = await fetch(`${API_BASE_URL}/jobs/${encodeURIComponent(jobGuid)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
    signal,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({
      error: `Failed to update job: ${response.status}`,
      status: response.status,
    }));
    throw new JobCreationError(response.status, errorData.error, errorData.error);
  }

  return response.json();
}

export async function getJobCreationStatus(
  messageId: string,
  signal?: AbortSignal
): Promise<JobCreationStatusResponse> {
  const response = await fetch(
    `${API_BASE_URL}/jobs/messages/${encodeURIComponent(messageId)}`,
    {
      method: 'GET',
      headers: { Accept: 'application/json' },
      signal,
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({
      error: `Failed to get status: ${response.status}`,
      status: response.status,
    }));
    throw new JobCreationError(response.status, errorData.error, errorData.error);
  }

  return response.json();
}
